import { useEffect, useState, useCallback } from "react";
import classes from "./Overview.module.css";
import LoadingSpinnerIcon from "../../icons/LoadingSpinnerIcon";
import PieChartCard from "./PieChartCard";
import { env } from "../../constants/env";
import { useDynamicTitle } from "../../hooks/useDynamicTitle";

function formatM(value) {
  if (value == null) return '—';
  const num = parseFloat(value);
  return `${num % 1 === 0 ? num : num.toFixed(2)}M`;
}

function formatCurrency(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatLots(value) {
  if (value == null) return '—';
  const num = parseFloat(value);
  return num % 1 === 0 ? num.toString() : num.toFixed(2);
}

function nowTimestamp() {
  return new Date().toLocaleString('en-US', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const Overview = ({ themeMode, title = "Overview", titlePath = "dashboard/overview" }) => {
  useDynamicTitle(title, titlePath);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timestamp, setTimestamp] = useState(nowTimestamp());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${env.primeBridgeUrl}/api/dashboard`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      setData(json);
      setTimestamp(nowTimestamp());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const dash = (v) => (loading ? '—' : v);

  return (
    <div className="main-divs">
      {loading && !data && (
        <div className={`${classes.loadingWrapper} d-flex flex-column justify-content-center align-items-center gap-4`}>
          <span className={classes.loader}></span>
          <p>Optimizing assets, please standby</p>
        </div>
      )}

      {(!loading || data) && (
        <div className={classes.wrapper}>
          <div className={classes.mainHeader}>
            <div>
              <h3>{title}</h3>
              <p className={classes.timestamp}>Last updated: {timestamp}</p>
            </div>
            <div
              className={`${classes.refreshButton} d-flex align-items-center gap-2`}
              role="button"
              onClick={fetchData}
            >
              <LoadingSpinnerIcon width="16" height="17" color="var(--primary-color)" />
              {loading ? 'Loading…' : 'Refresh'}
            </div>
          </div>

          {error && <div className={classes.errorBanner}>Failed to load data: {error}</div>}

          <div className={classes.topRow}>
            <PieChartCard data={d.pieChart ?? []} />
            <div className={`${classes.card} d-flex flex-column justify-content-center align-items-center`}>
              <h1>{dash(formatM(d.aum))}</h1>
              <p>Assets Under Management</p>
            </div>
          </div>

          <div className={classes.bottomRow}>
            {/* Row 1 — three equal-height cards */}
            <div className={`${classes.card} ${classes.metricCard} d-flex flex-column justify-content-center align-items-center`}>
              <h2>{dash(formatLots(d.totalNetVolume))}</h2>
              <p>Net Volume</p>
            </div>

            <div className={`${classes.card} ${classes.metricCard} d-flex flex-column justify-content-center align-items-center`}>
              <h2>{dash(formatM(d.totalNOP))}</h2>
              <p>Total Net Open Positions</p>
            </div>

            <div className={`${classes.card} ${classes.metricCard} d-flex flex-column justify-content-center align-items-center`}>
              <h2>{dash(formatCurrency(d.profitWithdrawals))}</h2>
              <p>Profit Withdrawals</p>
            </div>

            {/* Row 2 — capacity text beneath left and right cards */}
            <div className={classes.capacityInfo}>
              <p>Current NOP Limit – <span className={classes.highlight}>{dash(formatM(d.nopLimit))}</span></p>
              <p>Approximate Lots Limit – <span className={classes.highlight}>{dash(formatLots(d.approxLotsLimit))}</span></p>
            </div>

            <div />

            <div className={`${classes.capacityInfo} ${classes.alignRight}`}>
              <p>Remaining NOP Capacity – <span className={classes.highlight}>{dash(formatM(d.remainingNOP))}</span></p>
              <p>Remaining Lots Capacity – <span className={classes.highlight}>{dash(formatLots(d.remainingLots))}</span></p>
            </div>
          </div>

          <div className={classes.legalLinks}>
            <a href="#">Legal &amp; Privacy</a>
            <a href="#">Risk Disclosure</a>
            <span className={classes.copyright}>@ 2023 Prime Bridge</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
